import { useItemDetailsModal } from '@sky-distribution/onboarding-commons-ui';
import { OfferDetailItem } from '@sky-distribution/onboarding-commons-ui/lib/ItemDetailsModal/ItemDetailsModal.types';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CommonInfoIconStyled } from '../../CommonInfoIcon/CommonInfoIcon.styled';
import { getInitialSelectedPlanIndex, getOriginalCoverageOptionCodeById } from '../CoverageInfoModal.utils';
import modalAssets from '../CoverageInfoModalAssets';
import { SelectCoveragePlanHeader } from '../SelectCoveragePlanHeader/SelectCoveragePlanHeader';

export interface CoverageInfoIconProps {
  modalDetails: OfferDetailItem | OfferDetailItem[];
  selectCoveragePlan?: (coverageOptionCode: string) => void;
  chosenTermValue?: string;
}

const FORM_NS = 'form';

export const CoverageInfoIcon = ({
  modalDetails,
  selectCoveragePlan,
  chosenTermValue,
}: CoverageInfoIconProps): JSX.Element => {
  const { t } = useTranslation(FORM_NS);
  const isModalDetailsArray = Array.isArray(modalDetails);
  const submitModalButtonText = (isModalDetailsArray && chosenTermValue)
    ? t('CoverageInfoModal.Button.Choose')
    : t('CoverageInfoModal.Button.Close');
  const initialSelectedPlanIndex = getInitialSelectedPlanIndex(
    modalDetails,
    t,
    chosenTermValue,
  );

  const [selectedPlanIndex, setSelectedPlanIndex] = useState(initialSelectedPlanIndex);
  const {
    component, setIsVisible, setItem, isVisible,
  } = useItemDetailsModal({
    buttonText: submitModalButtonText,
    assets: modalAssets,
    shouldSaveHistory: false,
  });

  const handlePlanChoice = ({ chosenPlanIndex, details }: {
    chosenPlanIndex?: number;
    details?: OfferDetailItem;
  }) => {
    if (typeof chosenPlanIndex !== 'undefined') {
      setSelectedPlanIndex(chosenPlanIndex);
    }

    if (isModalDetailsArray && details) {
      const coverageOptionCode = getOriginalCoverageOptionCodeById(
        modalDetails,
        details.id,
      );

      if (coverageOptionCode && selectCoveragePlan) {
        selectCoveragePlan(coverageOptionCode);
      }
    }

    setIsVisible(!isVisible);
  };

  const handleCoveragePlanChange = (chosenPlanIndex: number): void => {
    if (isModalDetailsArray) {
      setItem(modalDetails[chosenPlanIndex], {
        onAccept: (details) => handlePlanChoice({ chosenPlanIndex, details }),
        subHeader: (
          <SelectCoveragePlanHeader
            coveragePlans={modalDetails}
            selectedPlanIndex={chosenPlanIndex}
            onChange={handleCoveragePlanChange}
            t={t}
          />
        ),
      });
    }
  };

  const handleCoverageInfoClick = (): void => {
    setIsVisible(!isVisible);

    const coverageDetails = isModalDetailsArray
      ? modalDetails[selectedPlanIndex]
      : modalDetails;

    setItem(coverageDetails, {
      onAccept: (details) => handlePlanChoice({ details }),
      subHeader: (isModalDetailsArray && selectCoveragePlan) && (
        <SelectCoveragePlanHeader
          coveragePlans={modalDetails}
          selectedPlanIndex={selectedPlanIndex}
          onChange={handleCoveragePlanChange}
          t={t}
        />
      ),
    });
  };

  return (
    <>
      <CommonInfoIconStyled
        data-testid='coverage-info-icon'
        onClick={handleCoverageInfoClick}
      />

      {isVisible && component}
    </>
  );
};
